// ADV-1157: "Diagnose this run" panel for the import review page. Manual
// trigger only (no auto-fire) to manage cost. Composes shadcn Card / Button /
// Alert / Skeleton — no hand-rolled controls (ADR-0018/0019). The diagnosis
// runs server-side; this panel just triggers and renders.

import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { ImportDiagnosis, ImportJobStatus } from "@adva/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useAbility } from "@/hooks/use-ability";
import {
  useCancelImportJob,
  useDiagnoseImport,
  useFileImportTicket,
  useRetryNormalization,
} from "@/hooks/use-import";
import { DiagnosisResult } from "./diagnosis-result";

const RETRYABLE: ReadonlySet<ImportJobStatus> = new Set([
  "failed",
  "expired",
] as ImportJobStatus[]);

export function DiagnosisPanel({
  jobId,
  status,
}: {
  jobId: string;
  status: ImportJobStatus;
}) {
  const ability = useAbility();
  const diagnose = useDiagnoseImport(jobId);
  const retry = useRetryNormalization(jobId);
  const cancel = useCancelImportJob(jobId);
  const fileTicket = useFileImportTicket(jobId);
  const [diagnosis, setDiagnosis] = useState<ImportDiagnosis | null>(null);

  // ADV-1165: super-admins (Adva staff) get the operator-grade diagnosis;
  // every tenant user gets the plain-language register. `manage all` is granted
  // only to super_admin (packages/auth/src/abilities.ts).
  const isOperator = ability.can("manage", "all");
  const register = isOperator ? "operator" : "user";
  // Filing an operator-grade internal ticket is super-admin-only now (was also
  // account owners pre-ADV-1165). The user register hides the action entirely.
  const canFileTicket = isOperator;

  const runDiagnosis = () => {
    // The server replays a stored diagnosis for the life of the job. The first
    // run omits refresh (replay the cache, no token spend); once a diagnosis is
    // shown the button reads "Re-run diagnosis" and forces a fresh run.
    diagnose.mutate(
      { refresh: diagnosis !== null },
      {
        onSuccess: (res) => setDiagnosis(res.diagnosis),
        onError: (e) =>
          toast.error(
            e instanceof Error ? e.message : "Could not run diagnosis",
          ),
      },
    );
  };

  const handleRetry = () => {
    retry.mutate(undefined, {
      onSuccess: () => toast.success("Normalization retry dispatched"),
      onError: (e) =>
        toast.error(e instanceof Error ? e.message : "Retry failed"),
    });
  };

  const handleCancel = () => {
    cancel.mutate(undefined, {
      onSuccess: () => toast.success("Import job cancelled"),
      onError: (e) =>
        toast.error(e instanceof Error ? e.message : "Cancel failed"),
    });
  };

  const handleFileTicket = () => {
    const ft = diagnosis?.file_linear_ticket;
    if (!ft?.title || !ft.body) {
      toast.error("No ticket title/body in the diagnosis");
      return;
    }
    fileTicket.mutate(
      { title: ft.title, body: ft.body },
      {
        onSuccess: (res) =>
          res.filed
            ? toast.success(`Filed ${res.identifier ?? "ticket"}`)
            : toast.message(
                "Ticket captured — file manually (Linear not configured)",
              ),
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : "Filing failed"),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles
                className="size-4 text-[color:var(--color-accent)]"
                aria-hidden="true"
              />
              AI diagnosis
            </CardTitle>
            <CardDescription>
              Assemble this run&apos;s reconciliation, errors, and logs into a
              targeted root-cause analysis with one-click remediation.
            </CardDescription>
          </div>
          <Button onClick={runDiagnosis} disabled={diagnose.isPending}>
            {diagnose.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Diagnosing…
              </>
            ) : diagnosis ? (
              "Re-run diagnosis"
            ) : (
              "Diagnose this run"
            )}
          </Button>
        </div>
      </CardHeader>

      {(diagnose.isPending || diagnose.isError || diagnosis) && (
        <CardContent>
          {diagnose.isPending && (
            <div className="space-y-3" aria-label="Running diagnosis">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {diagnose.isError && !diagnose.isPending && (
            <Alert variant="destructive">
              <AlertTitle>Diagnosis failed</AlertTitle>
              <AlertDescription>
                {diagnose.error instanceof Error
                  ? diagnose.error.message
                  : "The diagnosis could not be produced. Try again shortly."}
              </AlertDescription>
            </Alert>
          )}

          {diagnosis && !diagnose.isPending && (
            <DiagnosisResult
              diagnosis={diagnosis}
              register={register}
              handlers={{
                onRetry: handleRetry,
                onCancel: handleCancel,
                onFileTicket: handleFileTicket,
                retrying: retry.isPending,
                cancelling: cancel.isPending,
                filingTicket: fileTicket.isPending,
                canFileTicket,
                canRetry: RETRYABLE.has(status),
              }}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}
